import React from 'react';
import { BrowserRouter, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { Header } from './components/layout/Header';
import { Footer } from './components/layout/Footer';
import { DashboardLayout } from './components/layout/DashboardLayout';

// Public Pages
import Landing from './pages/Landing';
import Products from './pages/Products';
import Pricing from './pages/Pricing';
import Features from './pages/Features';
import HowItWorks from './pages/HowItWorks';
import FAQ from './pages/FAQ';
import Contact from './pages/Contact';

// Product Detail Page
import ProductDetail from './pages/ProductDetail';

// Auth Pages
import Login from './pages/Login';
import AuthCallback from './pages/AuthCallback';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';

// Owner Dashboard Pages
import OwnerDashboard from './pages/owner/OwnerDashboard';
import ProfileEditor from './pages/owner/ProfileEditor';
import SubscriptionsPage from './pages/owner/SubscriptionsPage';
import BuyMoreCards from './pages/owner/BuyMoreCards';
import OwnerSettings from './pages/owner/OwnerSettings';

// Admin Dashboard Pages
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminSettings from './pages/admin/AdminSettings';
import AdminCoupons from './pages/admin/AdminCoupons';

// Public Profile Edit (Secure Link)
import PublicProfileEdit from './pages/PublicProfileEdit';

// Legacy Dashboard Pages (redirect to owner)
import DashboardOverview from './pages/dashboard/Overview';
import ProfilePage from './pages/dashboard/Profile';
import CardPage from './pages/dashboard/Card';
import OrdersPage from './pages/dashboard/Orders';
import SettingsPage from './pages/dashboard/Settings';

// Other Pages
import PublicProfile from './pages/PublicProfile';
import CheckoutSuccess from './pages/CheckoutSuccess';
import CheckoutCancel from './pages/CheckoutCancel';
import CheckoutPending from './pages/CheckoutPending';
import AccountPending from './pages/AccountPending';
import NotFound from './pages/NotFound';

// Legal Pages
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';

// Haiti Mondial Landing Page
import HaitiMondial from './pages/HaitiMondial';

// Protected Route Component
const ProtectedRoute = ({ children, requireAdmin = false }) => {
  const { user, loading, isAuthenticated } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  if (requireAdmin && user?.role !== 'admin') {
    return <Navigate to="/owner" replace />;
  }
  
  return children;
};

import './App.css';

// Layout wrapper for public pages
const PublicLayout = ({ children }) => {
  const location = useLocation();
  const hideHeaderFooter = location.pathname.startsWith('/user/') || 
                           location.pathname.startsWith('/auth/');
  
  return (
    <>
      {!hideHeaderFooter && <Header />}
      <main className={!hideHeaderFooter ? 'pt-16' : ''}>
        {children}
      </main>
      {!hideHeaderFooter && <Footer />}
    </>
  );
};

// Conditional route: redirect authenticated users from /products to /owner/buy-cards
const ProductsRoute = () => {
  const { isAuthenticated } = useAuth();
  if (isAuthenticated) {
    return <Navigate to="/owner/buy-cards" replace />;
  }
  return <PublicLayout><Products /></PublicLayout>;
};

// Product detail route: redirect authenticated users from /products/:type to /owner/buy-cards
const ProductDetailRoute = () => {
  const { isAuthenticated } = useAuth();
  if (isAuthenticated) {
    return <Navigate to="/owner/buy-cards" replace />;
  }
  return <PublicLayout><ProductDetail /></PublicLayout>;
};

// Main App Router
const AppRouter = () => {
  const location = useLocation();
  
  // Check for OAuth callback - handle session_id in URL fragment
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Standalone Landing Pages (no header/footer) */}
      <Route path="/haiti-mondial" element={<HaitiMondial />} />
      <Route path="/haiti" element={<HaitiMondial />} />

      {/* Public Routes */}
      <Route path="/" element={<PublicLayout><Landing /></PublicLayout>} />
      <Route path="/products" element={<ProductsRoute />} />
      <Route path="/products/:productType" element={<ProductDetailRoute />} />
      <Route path="/pricing" element={<PublicLayout><Pricing /></PublicLayout>} />
      <Route path="/features" element={<PublicLayout><Features /></PublicLayout>} />
      <Route path="/how-it-works" element={<PublicLayout><HowItWorks /></PublicLayout>} />
      <Route path="/faq" element={<PublicLayout><FAQ /></PublicLayout>} />
      <Route path="/contact" element={<PublicLayout><Contact /></PublicLayout>} />
      
      {/* Legal Pages */}
      <Route path="/privacy" element={<PublicLayout><PrivacyPolicy /></PublicLayout>} />
      <Route path="/terms" element={<PublicLayout><TermsOfService /></PublicLayout>} />
      
      {/* Auth Routes */}
      <Route path="/login" element={<PublicLayout><Login /></PublicLayout>} />
      <Route path="/forgot-password" element={<PublicLayout><ForgotPassword /></PublicLayout>} />
      <Route path="/reset-password" element={<PublicLayout><ResetPassword /></PublicLayout>} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      
      {/* Checkout */}
      <Route path="/checkout/success" element={<PublicLayout><CheckoutSuccess /></PublicLayout>} />
      <Route path="/checkout/cancel" element={<PublicLayout><CheckoutCancel /></PublicLayout>} />
      
      {/* Public Profile */}
      <Route path="/user/:username" element={<PublicProfile />} />
      
      {/* Owner Dashboard Routes */}
      <Route path="/owner" element={
        <ProtectedRoute>
          <PublicLayout><OwnerDashboard /></PublicLayout>
        </ProtectedRoute>
      } />
      <Route path="/owner/profile/:profileId" element={
        <ProtectedRoute>
          <PublicLayout><ProfileEditor /></PublicLayout>
        </ProtectedRoute>
      } />
      <Route path="/owner/subscriptions" element={
        <ProtectedRoute>
          <PublicLayout><SubscriptionsPage /></PublicLayout>
        </ProtectedRoute>
      } />
      <Route path="/owner/buy-cards" element={
        <ProtectedRoute>
          <PublicLayout><BuyMoreCards /></PublicLayout>
        </ProtectedRoute>
      } />
      <Route path="/owner/settings" element={
        <ProtectedRoute>
          <PublicLayout><OwnerSettings /></PublicLayout>
        </ProtectedRoute>
      } />
      
      {/* Admin Dashboard Routes */}
      <Route path="/admin" element={
        <ProtectedRoute requireAdmin>
          <PublicLayout><AdminDashboard /></PublicLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/settings" element={
        <ProtectedRoute requireAdmin>
          <PublicLayout><AdminSettings /></PublicLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/coupons" element={
        <ProtectedRoute requireAdmin>
          <PublicLayout><AdminCoupons /></PublicLayout>
        </ProtectedRoute>
      } />
      
      {/* Public Profile Edit (Secure Link - No Auth Required) */}
      <Route path="/profile/edit/:token" element={<PublicProfileEdit />} />
      
      {/* Account Pending Page */}
      <Route path="/account-pending" element={<PublicLayout><AccountPending /></PublicLayout>} />
      
      {/* Checkout Pages */}
      <Route path="/checkout/pending" element={<PublicLayout><CheckoutPending /></PublicLayout>} />
      
      {/* Legacy Dashboard Routes - Redirect to new Owner dashboard */}
      <Route path="/dashboard" element={<Navigate to="/owner" replace />} />
      <Route path="/dashboard/*" element={<Navigate to="/owner" replace />} />
      <Route path="/register" element={<Navigate to="/login" replace />} />
      
      {/* 404 */}
      <Route path="*" element={<PublicLayout><NotFound /></PublicLayout>} />
    </Routes>
  );
};

function App() {
  return (
    <BrowserRouter>
      <LanguageProvider>
        <AuthProvider>
          <div className="App min-h-screen bg-background">
            <AppRouter />
            <Toaster 
              position="top-right" 
              richColors 
              closeButton
              toastOptions={{
                style: {
                  borderRadius: '12px',
                }
              }}
            />
          </div>
        </AuthProvider>
      </LanguageProvider>
    </BrowserRouter>
  );
}

export default App;
